import InputLabelField from '@/components/form-control/InputLabelField'
import { useAuthContext } from '@/features/auth/context'
import { useConfigUI } from '@/features/config/context'
import { useAddEmitter } from '@/features/config/hooks/emitter'
import useEmitterById from '@/features/config/hooks/emitter/useEmitterById'
import useUpdateEmitter from '@/features/config/hooks/emitter/useUpdateEmitter'
import useTranslation from '@/hooks/useTranslation'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

interface EmitterViewProps {
  id?: string
}

const schema = yup
  .object({
    emitter: yup.string().trim().required('Emitter is required'),
    efficiency: yup
      .number()
      // .integer('Efficiency is an integer')
      .transform((value, originalValue) => {
        return originalValue === '' ? undefined : value
      })
      .typeError('Efficiency is a number')
      .min(0, 'Minimum 0')
      .max(100, 'Maximum 100'),
    // .required('Efficiency is required'),
  })
  .required()

const EmitterView = ({ id }: EmitterViewProps) => {
  const { t } = useTranslation()
  const { closeSidebar } = useConfigUI()
  const { interfaceId } = useAuthContext()

  const inputRefSiteName = useRef<HTMLInputElement>(null)

  const { mutate: addEmitter, isLoading: loadingAddEmitter } = useAddEmitter()

  const { mutate: editEmitter, isLoading: loadingEditEmitter } =
    useUpdateEmitter()

  const { data: editItem, isLoading: isLoadingEmitter } = useEmitterById(
    interfaceId,
    id,
  )

  useEffect(() => {
    if (inputRefSiteName.current) {
      inputRefSiteName.current.focus()
    }
  }, [])

  const mode = id ? 'edit' : 'create'
  const isLoading = loadingAddEmitter || loadingEditEmitter

  const defaultFormValues = {
    id: '',
    interfaceId: '',
    emitter: '',
    efficiency: '',
  }

  const form = useForm({
    defaultValues: {
      ...defaultFormValues,
      ...editItem,
    },
    resolver: yupResolver(schema),
  })
  useEffect(() => {
    if (editItem?.id) {
      form.reset({
        id: editItem.id,
        interfaceId: editItem.interfaceId,
        emitter: editItem.emitter,
        efficiency: editItem.efficiency,
      })
    } else {
      form.reset(defaultFormValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editItem])

  const handleSave = (data: any) => {
    if (mode === 'create') {
      addEmitter({
        interfaceId: Number(interfaceId||0),
        emitter: data.emitter,
        efficiency: data.efficiency,
      })
    }
    if (mode === 'edit') {
      editEmitter({
        id: Number(id),
        interfaceId: Number(interfaceId||0),
        emitter: data.emitter,
        efficiency: data.efficiency,
      })
    }
  }

  return (
    <Container sx={{ maxWidth: '450px', height: '100vh' }}>
      {isLoadingEmitter && (
        <Stack
          width="377px"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Stack>
      )}
      {!isLoadingEmitter && (
        <form onSubmit={form.handleSubmit(handleSave)}>
          <Stack sx={{ padding: '34px 18px' }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: '377px', marginBottom: '20px' }}
            >
              <Typography color="text.main">
                {id ? t('dataConfigEditEmitter') : t('dataConfigAddEmitter')}
              </Typography>
              <Stack direction="row">
                <Button
                  variant="secondary"
                  size="small"
                  sx={{ marginRight: '7px', width: '85px' }}
                  onClick={() => closeSidebar()}
                >
                  {t('commonActionsCancel')}
                </Button>
                <LoadingButton
                  loading={isLoading}
                  size="small"
                  variant="primary"
                  sx={{ minWidth: '72px' }}
                  type="submit"
                >
                  {t('commonActionsSave')}
                </LoadingButton>
              </Stack>
            </Stack>

            <InputLabelField
              isRequired
              name="emitter"
              form={form}
              label={t('formsLabelsEmitter')}
              placeholder={t('formsLabelsEmitter')}
              inputRef={inputRefSiteName}
              style={{
                height: 50,
                borderRadius: '4px',
                width: { xs: '377px' },
                marginTop: '8px',
              }}
              padding="8px 16px"
            />

            <InputLabelField
              name="efficiency"
              form={form}
              label={t('tableHeaderEfficiency')}
              placeholder={t('tableHeaderEfficiency')}
              style={{
                height: 50,
                borderRadius: '4px',
                width: { xs: '377px' },
                marginTop: '8px',
              }}
              padding="8px 16px"
            />
          </Stack>
        </form>
      )}
    </Container>
  )
}

export default EmitterView
